(() => {
  const SECTION_SELECTOR = '[data-hormonize-product-showcase]';
  const THUMB_SELECTOR = '[data-showcase-thumb]';
  const AUTOPLAY_MS = 1500;
  const DEFAULT_TRANSITION_MS = 150;

  const instances = new WeakMap();

  class HormonizeProductShowcaseController {
    constructor(section) {
      this.section = section;
      this.thumbs = Array.from(section.querySelectorAll(THUMB_SELECTOR));
      this.card = section.querySelector('[data-showcase-card]');
      this.image = section.querySelector('[data-showcase-image]');
      this.cta = section.querySelector('[data-showcase-cta]');
      this.ctaLabel = section.querySelector('[data-showcase-cta-label]');
      this.pill = section.querySelector('[data-showcase-pill]');
      this.pillLabel = section.querySelector('[data-showcase-pill-label]');
      this.peekLeft = section.querySelector('[data-showcase-peek="left"]');
      this.peekRight = section.querySelector('[data-showcase-peek="right"]');
      this.peekLeftImage = this.peekLeft?.querySelector('[data-showcase-peek-image]') || null;
      this.peekRightImage = this.peekRight?.querySelector('[data-showcase-peek-image]') || null;
      this.activeIndex = Math.max(
        0,
        this.thumbs.findIndex((thumb) => thumb.classList.contains('is-active'))
      );
      this.autoplayEnabled = section.dataset.autoplay === 'true';
      this.transitionMs = Number.parseInt(section.dataset.transitionMs || '', 10);
      if (!Number.isFinite(this.transitionMs) || this.transitionMs < 0) {
        this.transitionMs = DEFAULT_TRANSITION_MS;
      }
      /* Cap transition so Theme Editor values stay within the intended shift speed. */
      this.transitionMs = Math.min(this.transitionMs, DEFAULT_TRANSITION_MS);
      this.autoplayTimer = null;
      this.observer = null;
      this.isInView = false;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onThumbClick = this.onThumbClick.bind(this);
      this.onThumbKeydown = this.onThumbKeydown.bind(this);
      this.onPointerEnter = this.pauseAutoplay.bind(this);
      this.onPointerLeave = this.resumeAutoplay.bind(this);
      this.onFocusIn = this.pauseAutoplay.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);

      this.bind();
      this.setupObserver();
      this.updatePeeks(this.activeIndex);
      if (this.pillLabel) {
        this.pillLabel.textContent = this.stripHtml(this.pillLabel.textContent);
      }
      this.prefersReducedMotion.addEventListener('change', this.onReducedMotionChange);
    }

    bind() {
      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.onThumbClick(index));
        thumb.addEventListener('keydown', (event) => this.onThumbKeydown(event, index));
      });

      this.section.addEventListener('pointerenter', this.onPointerEnter);
      this.section.addEventListener('pointerleave', this.onPointerLeave);
      this.section.addEventListener('focusin', this.onFocusIn);
      this.section.addEventListener('focusout', this.onFocusOut);
    }

    setupObserver() {
      if (!('IntersectionObserver' in window)) {
        this.isInView = true;
        this.resumeAutoplay();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isInView = Boolean(entry && entry.isIntersecting);
          if (this.isInView) {
            this.resumeAutoplay();
          } else {
            this.pauseAutoplay();
          }
        },
        { threshold: 0.35 }
      );

      this.observer.observe(this.section);
    }

    onThumbClick(index) {
      this.select(index, { focus: false, userInitiated: true });
    }

    onThumbKeydown(event, index) {
      const key = event.key;
      let nextIndex = null;

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        nextIndex = (index + 1) % this.thumbs.length;
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        nextIndex = (index - 1 + this.thumbs.length) % this.thumbs.length;
      } else if (key === 'Home') {
        nextIndex = 0;
      } else if (key === 'End') {
        nextIndex = this.thumbs.length - 1;
      } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.select(index, { focus: false, userInitiated: true });
        return;
      }

      if (nextIndex === null) {
        return;
      }

      event.preventDefault();
      this.select(nextIndex, { focus: true, userInitiated: true });
    }

    select(index, { focus = false, userInitiated = false } = {}) {
      if (!this.thumbs.length || index < 0 || index >= this.thumbs.length) {
        return;
      }

      const thumb = this.thumbs[index];
      this.activeIndex = index;

      this.thumbs.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.tabIndex = isActive ? 0 : -1;
      });

      if (this.card && thumb.id) {
        this.card.setAttribute('aria-labelledby', thumb.id);
      }

      this.updateCard(thumb);
      this.updatePeeks(index);

      if (focus) {
        thumb.focus({ preventScroll: true });
      }

      if (userInitiated) {
        this.pauseAutoplay();
        this.resumeAutoplay();
      }
    }

    stripHtml(value) {
      return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    updatePeeks(index) {
      const prevThumb = this.thumbs[(index - 1 + this.thumbs.length) % this.thumbs.length];
      const nextThumb = this.thumbs[(index + 1) % this.thumbs.length];
      const hasNeighbors = this.thumbs.length > 1;

      this.applyPeek(this.peekLeft, this.peekLeftImage, hasNeighbors ? prevThumb : null);
      this.applyPeek(this.peekRight, this.peekRightImage, hasNeighbors ? nextThumb : null);
    }

    applyPeek(peek, image, thumb) {
      if (!peek || !image) {
        return;
      }

      if (!thumb || !thumb.dataset.imageSrc) {
        peek.classList.add('is-empty');
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        return;
      }

      const imageSrc = thumb.dataset.imageSrc;
      const imageSrc2x = thumb.dataset.imageSrc2x || imageSrc;
      peek.classList.remove('is-empty');
      image.src = imageSrc;
      image.srcset = `${imageSrc} 400w, ${imageSrc2x} 600w`;
      image.alt = '';
    }

    updateCard(thumb) {
      const url = thumb.dataset.productUrl || '#';
      const ctaLabel = this.stripHtml(thumb.dataset.ctaLabel || 'Shop now');
      const pillLabel = this.stripHtml(thumb.dataset.pillLabel || '');
      const imageSrc = thumb.dataset.imageSrc || '';
      const imageSrc2x = thumb.dataset.imageSrc2x || imageSrc;
      const imageAlt = this.stripHtml(thumb.dataset.imageAlt || thumb.dataset.productTitle || '');

      if (this.image) {
        this.image.classList.add('is-swapping');
      }
      if (this.pill) {
        this.pill.classList.add('is-swapping');
      }

      const apply = () => {
        if (this.cta) {
          this.cta.setAttribute('href', url);
        }
        if (this.ctaLabel) {
          this.ctaLabel.textContent = ctaLabel;
        }
        if (this.pillLabel) {
          this.pillLabel.textContent = pillLabel;
        }
        if (this.image && imageSrc) {
          this.image.src = imageSrc;
          this.image.srcset = `${imageSrc} 400w, ${imageSrc2x} 600w`;
          this.image.alt = imageAlt;
        }

        requestAnimationFrame(() => {
          if (this.image) {
            this.image.classList.remove('is-swapping');
          }
          if (this.pill) {
            this.pill.classList.remove('is-swapping');
          }
        });
      };

      const swapDelay = this.prefersReducedMotion.matches
        ? 0
        : Math.max(20, Math.round(this.transitionMs * 0.35));
      window.setTimeout(apply, swapDelay);
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        this.isInView &&
        this.thumbs.length > 1 &&
        !this.prefersReducedMotion.matches
      );
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    resumeAutoplay() {
      this.pauseAutoplay();
      if (!this.canAutoplay()) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        const nextIndex = (this.activeIndex + 1) % this.thumbs.length;
        this.select(nextIndex, { focus: false, userInitiated: false });
      }, AUTOPLAY_MS);
    }

    onFocusOut(event) {
      if (this.section.contains(event.relatedTarget)) {
        return;
      }
      this.resumeAutoplay();
    }

    onReducedMotionChange() {
      if (this.prefersReducedMotion.matches) {
        this.pauseAutoplay();
      } else {
        this.resumeAutoplay();
      }
    }

    destroy() {
      this.pauseAutoplay();
      this.observer?.disconnect();
      this.prefersReducedMotion.removeEventListener('change', this.onReducedMotionChange);
      this.section.removeEventListener('pointerenter', this.onPointerEnter);
      this.section.removeEventListener('pointerleave', this.onPointerLeave);
      this.section.removeEventListener('focusin', this.onFocusIn);
      this.section.removeEventListener('focusout', this.onFocusOut);
      this.thumbs = [];
      this.section = null;
    }
  }

  function initSection(section) {
    if (!(section instanceof HTMLElement) || !section.matches(SECTION_SELECTOR) || instances.has(section)) {
      return;
    }

    if (!section.querySelector(THUMB_SELECTOR)) {
      return;
    }

    instances.set(section, new HormonizeProductShowcaseController(section));
  }

  function initSections(root = document) {
    const searchRoot = root instanceof Element || root instanceof Document ? root : document;

    if (searchRoot instanceof Element && searchRoot.matches(SECTION_SELECTOR)) {
      initSection(searchRoot);
    }

    searchRoot.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  function destroySections(root) {
    const searchRoot = root instanceof Element ? root : document;
    const sections = [];

    if (searchRoot instanceof Element && searchRoot.matches(SECTION_SELECTOR)) {
      sections.push(searchRoot);
    }

    searchRoot.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      sections.push(section);
    });

    sections.forEach((section) => {
      const instance = instances.get(section);
      if (!instance) {
        return;
      }
      instance.destroy();
      instances.delete(section);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initSections());

  document.addEventListener('shopify:section:load', (event) => {
    initSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySections(event.target);
  });

  if (document.readyState !== 'loading') {
    initSections();
  }
})();
